import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Tv, RefreshCw, Smartphone, Monitor, Tablet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { formatRelative } from '@/lib/format';

interface TvSignIn {
  id: string;
  user_id: string | null;
  full_name: string | null;
  phone: string | null;
  role: string | null;
  device: string | null;
  ip: string | null;
  location: string | null;
  logged_in_at: string;
}

// Summarise a raw User-Agent into "Device · Browser" for humans.
function deviceSummary(ua: string | null): { label: string; kind: 'phone' | 'tablet' | 'desktop' } {
  if (!ua) return { label: 'Unknown device', kind: 'desktop' };
  const s = ua.toLowerCase();
  let os = 'Device';
  if (/iphone/.test(s)) os = 'iPhone';
  else if (/ipad/.test(s)) os = 'iPad';
  else if (/android/.test(s)) os = /mobile/.test(s) ? 'Android phone' : 'Android tablet';
  else if (/windows/.test(s)) os = 'Windows PC';
  else if (/macintosh|mac os/.test(s)) os = 'Mac';
  else if (/linux/.test(s)) os = 'Linux';
  let browser = '';
  if (/edg\//.test(s)) browser = 'Edge';
  else if (/chrome|crios/.test(s)) browser = 'Chrome';
  else if (/firefox|fxios/.test(s)) browser = 'Firefox';
  else if (/safari/.test(s)) browser = 'Safari';
  const kind: 'phone' | 'tablet' | 'desktop' =
    /iphone|android.*mobile/.test(s) ? 'phone' : /ipad|tablet/.test(s) ? 'tablet' : 'desktop';
  return { label: browser ? `${os} · ${browser}` : os, kind };
}

const ROLE_LABEL: Record<string, string> = {
  tv_display: 'TV Wall Display', fleet_officer: 'Operations Manager', admin: 'Admin',
  super_admin: 'Super Admin', accountant: 'Accountant', auditor: 'Viewer',
};

export default function TvSignInsPage() {
  const [rows, setRows] = useState<TvSignIn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await apiClient.get<{ items: TvSignIn[] }>('/api/v1/users/tv-access-log', { params: { limit: 300 } });
      setRows(data.items ?? []);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const DeviceIcon = ({ kind }: { kind: 'phone' | 'tablet' | 'desktop' }) =>
    kind === 'phone' ? <Smartphone className="h-4 w-4" /> : kind === 'tablet' ? <Tablet className="h-4 w-4" /> : <Monitor className="h-4 w-4" />;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-1">
        <Link to="/dashboard/settings/users" className="text-muted-foreground hover:text-foreground"><ArrowLeft className="h-5 w-5" /></Link>
        <Tv className="h-5 w-5 text-amber-500" />
        <h1 className="text-xl font-medium">TV sign-ins</h1>
        <div className="ml-auto">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-2">
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} /> Refresh
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Every sign-in to the JASR TV / wall (app.jasrhub.com/tv), with the device it came from. Newest first.
      </p>

      {error && (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm mb-4">{error}</div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
          No TV sign-ins recorded yet. They appear here the moment someone logs into the wall.
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 640 }}>
              <thead>
                <tr className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium px-3 py-2.5">When</th>
                  <th className="text-left font-medium px-3 py-2.5">Person</th>
                  <th className="text-left font-medium px-3 py-2.5">Device</th>
                  <th className="text-left font-medium px-3 py-2.5">IP</th>
                  <th className="text-left font-medium px-3 py-2.5">Location</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const d = deviceSummary(r.device);
                  return (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/20">
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div>{new Date(r.logged_in_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        <div className="text-xs text-muted-foreground">{formatRelative(r.logged_in_at)}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium">{r.full_name ?? '—'}</div>
                        <div className="text-xs text-muted-foreground">{r.phone ?? ''}{r.role ? ` · ${ROLE_LABEL[r.role] ?? r.role}` : ''}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2"><DeviceIcon kind={d.kind} /><span>{d.label}</span></div>
                        {r.device && <div className="text-[10px] font-mono text-muted-foreground max-w-xs truncate" title={r.device}>{r.device}</div>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.ip ?? '—'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{r.location ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
